import React, { useEffect, useState } from "react";
import { getChallengeHistory } from "../../../services/api";

const ChallengeHistoryModal = ({
  isOpen,
  onClose,
  groupId,
  liveAvatars = {},
  onSelectPastChallenge,
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!isOpen || !groupId) return;

    let isMounted = true;
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setErrorMsg("");
        const data = await getChallengeHistory(groupId);
        if (isMounted) {
          setHistory(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (isMounted) {
          setErrorMsg(err.response?.data?.message || err.message || "Failed to load challenge history.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchHistory();

    return () => {
      isMounted = false;
    };
  }, [isOpen, groupId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-100 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-violet-900 via-indigo-900 to-purple-950 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-400 text-amber-950 flex items-center justify-center text-lg shadow-sm">
              🏆
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-black tracking-tight leading-tight">
                Challenge Hall of Fame
              </h2>
              <p className="text-xs text-indigo-300 mt-0.5">Past sprint battles and podium champions</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white transition flex items-center justify-center"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark text-sm"></i>
          </button>
        </div>

        {/* Body List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <i className="fa-solid fa-circle-notch fa-spin text-3xl mb-3 text-indigo-600"></i>
              <p className="text-sm font-semibold text-gray-600">Loading hall of fame...</p>
            </div>
          ) : errorMsg ? (
            <div className="p-4 rounded-2xl bg-rose-50 text-rose-700 text-xs font-semibold text-center">
              {errorMsg}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-16 bg-gray-50/60 rounded-3xl border border-dashed border-gray-200">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-bold text-gray-800 text-sm">No Concluded Challenges Yet</h3>
              <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
                Once a challenge duration ends, the top champions and final standings will be preserved here.
              </p>
            </div>
          ) : (
            history.map((c) => {
              const dateStr = new Date(c.expiresAt || c.createdAt).toLocaleDateString([], {
                month: "short",
                day: "numeric",
                year: "numeric",
              });
              const podium = c.podium || [];

              return (
                <div
                  key={c._id}
                  className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition flex flex-col gap-3.5 group"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {c.status === "cancelled" ? "Cancelled" : "Concluded"}
                        </span>
                        <span className="text-xs text-gray-400">{dateStr}</span>
                      </div>
                      <h3 className="text-sm md:text-base font-extrabold text-gray-900 mt-1">
                        {c.title}
                      </h3>
                      <p className="text-xs text-gray-500 font-medium">
                        Module: <span className="text-gray-800 font-bold">{c.reviewerTitle}</span>
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => onSelectPastChallenge(c)}
                      className="px-3.5 py-2 rounded-xl text-xs font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white transition shadow-2xs flex items-center gap-1.5"
                    >
                      <i className="fa-solid fa-trophy text-amber-500 text-xs"></i>
                      <span>View Standings</span>
                    </button>
                  </div>

                  {/* Top 3 Podium Winners Preview */}
                  {podium.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-50">
                      <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mr-1">
                        Podium:
                      </span>
                      {podium.map((p, idx) => (
                        <div
                          key={p.attemptId || p.userId}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-gray-50 border border-gray-100 text-xs font-medium text-gray-700"
                        >
                          <span className="text-xs">
                            {idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉"}
                          </span>
                          <img
                            src={
                              liveAvatars[String(p.userId)] ||
                              p.avatar ||
                              `https://api.dicebear.com/9.x/adventurer/svg?seed=${p.firstName}&backgroundColor=b6e3f4`
                            }
                            alt={p.firstName}
                            className="w-5 h-5 rounded-full object-cover border border-gray-200"
                          />
                          <span className="font-semibold">{p.firstName}</span>
                          <span className="font-mono text-[11px] font-bold text-indigo-600">
                            {p.totalPoints} pts
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic pt-1">
                      No participants completed this challenge.
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between shrink-0">
          <span className="text-xs text-gray-400">
            {history.length} past challenge{history.length !== 1 ? "s" : ""}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-200 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChallengeHistoryModal;
